// buildCalendar — client side of the build-calendar edge function (Oct 16, 2026).
//
// Robert's "Reschedule build" button and the API-created Google Calendar event
// for a build. The server does the work (deal update, Google event, SRS
// re-send, homeowner text, RM notice, audit rows); this file is the typed
// wrapper plus the small reads the PM Board and Today tab need.
//
// Reads: build_calendar_events + build_reschedules (RLS: any signed-in staff).
// Writes: only through the edge function (service role).

import { supabase } from "./supabase";
import { fetchEdge, readEdgeJson } from "./edgeError";
import type { PaymentPlanKey } from "../../supabase/functions/build-calendar/eventText";
import type { FlaggedTrade } from "./trades";

export type ReasonKey = "weather" | "crew" | "materials" | "homeowner_request" | "other";

/** The reason select (Greg, Oct 16, 2026). The key is what gets stored and
 *  what the homeowner texts key their wording on; the note is internal. */
export const REASON_OPTIONS: Array<{ key: ReasonKey; label: string; hint: string }> = [
  { key: "weather",           label: "Weather",                 hint: "Rain, wind, or a forecast that isn't safe to roof in." },
  { key: "crew",              label: "Crew can't make it today", hint: "Guys out sick, the crew stretched too thin, a truck down." },
  { key: "materials",         label: "Materials",               hint: "The drop didn't land or came in wrong." },
  { key: "homeowner_request", label: "Homeowner request",       hint: "The homeowner asked for a different day." },
  { key: "other",             label: "Other",                   hint: "Anything else. Put the detail in the note." },
];
export const REASON_LABEL: Record<ReasonKey, string> = Object.fromEntries(REASON_OPTIONS.map((r) => [r.key, r.label])) as Record<ReasonKey, string>;

export interface BuildCalendarLink {
  deal_id: string;
  calendar_id: string;
  event_id: string;
  html_link: string | null;
  summary: string | null;
  start_date: string | null;
  source: "api" | "adopted";
  created_by: string | null;
  created_at: string;
  updated_at: string;
  last_synced_at: string | null;
  last_error: string | null;
}

export interface BuildReschedule {
  id: string;
  deal_id: string;
  old_build_date: string | null;
  new_build_date: string | null;
  /** Oct 26, 2026: last day of a multi-day build before / after (null = one day). */
  old_build_end_date?: string | null;
  new_build_end_date?: string | null;
  old_drop_date: string | null;
  new_drop_date: string | null;
  reason_key: ReasonKey;
  note: string | null;
  cancelled_on_site_at: string | null;
  calendar_event_id: string | null;
  calendar_status: string | null;
  calendar_error: string | null;
  srs_resent: boolean;
  srs_log_id: string | null;
  homeowner_texted: boolean;
  rm_notified: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  actor_id: string | null;
  created_at: string;
}

export interface CalendarStatus {
  ok: boolean;
  configured: boolean;
  mode: "oauth" | "service_account" | null;
  calendar_id: string | null;
  act_as: string | null;
  connected_as: string | null;
  oauth_client_ready: boolean;
  last_error: string | null;
  redirect_uri: string;
  /** The Other Trades calendar (v10, Oct 28, 2026): null until the trades setting is in. */
  trades_calendar_id?: string | null;
}

export interface CandidateEvent {
  id: string;
  summary: string;
  startDate: string | null;
  location: string | null;
  htmlLink: string | null;
}

export interface RescheduleResult {
  ok: boolean;
  reschedule_id: string | null;
  deal: {
    old_build_date: string | null; new_build_date: string | null;
    old_build_end_date?: string | null; new_build_end_date?: string | null;
    old_drop_date: string | null; new_drop_date: string | null; drop_notes: string;
  };
  calendar: {
    status: "updated" | "adopted" | "not_found" | "ambiguous" | "not_configured" | "error" | "skipped";
    event_id?: string; html_link?: string | null; candidates?: CandidateEvent[]; day_url?: string; error?: string;
  };
  srs: { status: "sent" | "skipped" | "no_prior_order" | "failed"; log_id?: string | null; subject?: string | null; error?: string };
  homeowner_text: { status: "sent" | "not_sent" | "skipped" | "failed"; reason?: string; error?: string };
  rm: { notified: number };
  /** v10: the job's trades whose day no longer fits the roof (flagged, never moved). */
  trades?: FlaggedTrade[];
}

export interface UpsertEventResult {
  ok: boolean;
  created: boolean;
  /** A hand-made event on the build date was linked and updated instead of making a second one. */
  adopted?: boolean;
  event_id: string;
  html_link: string | null;
  guests: string[];
}

// ── Schedule build (Oct 19, 2026) ───────────────────────────────────────────

export {
  PAYMENT_PLAN_KEYS, PAYMENT_PLAN_LABELS, type PaymentPlanKey,
  MAX_BUILD_SPAN_DAYS, normalizeBuildEnd, buildEndProblem, buildDayCount, addWeekdaysISO,
} from "../../supabase/functions/build-calendar/eventText";

/** Mirror of build-calendar's SCHEDULING_ROLES (super users resolve to admin there). */
const SCHEDULING_ROLES = new Set(["roof_team_manager", "admin", "sales_director", "director_of_operations", "roof_director"]);
export function canScheduleBuilds(role: string | null | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || (!!role && SCHEDULING_ROLES.has(role));
}

/** Stages a build can be scheduled from (mirror of build-calendar SCHEDULE_FROM).
 *  One route (Oct 22, 2026): only an approved build. Before approval the PM
 *  sheet points at the Build Approvals board instead of offering a schedule. */
export const SCHEDULE_FROM_STAGES = new Set(["schedule_build", "build_scheduled"]);
/** Not submitted yet, or waiting for approval: the PM sheet says so instead of a Schedule button. */
export const BEFORE_APPROVAL_STAGES = new Set(["install_meeting", "build_submitted"]);

export interface ScheduleResult {
  ok: boolean;
  schedule_id: string | null;
  deal: { build_date: string; build_end_date?: string | null; drop_date: string; drop_notes: string; stage_before: string; skipped_approval: boolean };
  /** v7: the first payment plan used (null when the payment is in). source
   *  "submission" = recorded when the build was submitted; "schedule" = picked in the window. */
  payment_plan?: { plan: PaymentPlanKey; note: string | null; id: string | null; source: "submission" | "schedule" | null } | null;
  calendar: {
    status: "created" | "updated" | "adopted" | "not_configured" | "error" | "skipped";
    event_id?: string; html_link?: string | null; day_url?: string; error?: string;
  };
  srs: {
    status: "sent" | "skipped" | "already_sent" | "empty_order" | "failed";
    subject?: string | null; log_id?: string | null; line_count?: number | null; error?: string;
    prior_sent_at?: string; prior_build_date?: string;
  };
  /** already_texted (build-calendar v6): the automatic build text told them this
   *  date at `texted_at`. payment_only: the text was just the first payment line. */
  homeowner_text: {
    status: "sent" | "queued" | "not_sent" | "skipped" | "failed" | "already_texted";
    reason?: string; error?: string; send_at?: string; texted_at?: string; payment_only?: boolean;
  };
  chad: { status: "texted" | "notified" | "skipped" | "self" | "no_chad" | "failed"; error?: string };
  rm: { notified: number };
  /** v10: the job's trades whose day no longer fits the roof (flagged, never moved). */
  trades?: FlaggedTrade[];
}

export interface BuildSchedule {
  id: string;
  deal_id: string;
  build_date: string;
  build_end_date?: string | null;
  drop_date: string | null;
  drop_notes: string | null;
  stage_before: string;
  skipped_approval: boolean;
  first_payment_label: string | null;
  first_payment_due: number | null;
  payment_plan: PaymentPlanKey | null;
  /** v7: the build_payment_plans row used, and its note (Other's reason). */
  payment_plan_id?: string | null;
  payment_plan_note?: string | null;
  calendar_status: string | null;
  srs_status: string | null;
  homeowner_text_status: string | null;
  homeowner_text_send_at: string | null;
  chad_status: string | null;
  rm_notified: number;
  actor_id: string | null;
  created_at: string;
}

/** The latest Schedule build record for a deal, or null. */
export async function fetchLatestSchedule(dealId: string): Promise<BuildSchedule | null> {
  const { data, error } = await supabase.from("build_schedules").select("*")
    .eq("deal_id", dealId).order("created_at", { ascending: false }).limit(1).maybeSingle();
  if (error) { console.warn("[buildCalendar] schedule lookup failed:", error.message); return null; }
  if (!data) return null;
  const row = data as BuildSchedule;
  return { ...row, first_payment_due: row.first_payment_due == null ? null : Number(row.first_payment_due) };
}

/**
 * Call the build-calendar edge function. Throws an Error whose message is
 * safe to show (the function's `detail`, or a mapped code).
 */
export async function buildCalendar<T = Record<string, unknown>>(body: Record<string, unknown>): Promise<T> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) throw new Error("You've been signed out — sign back in and try again.");

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/build-calendar`;
  const res = await fetchEdge(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const out = await readEdgeJson<Record<string, unknown>>(res);
  if (!res.ok || !out?.ok) {
    const code = (out?.error as string) || "";
    const detail = (out?.detail as string) || (out?.message as string) || "";
    if (code === "not_configured") throw new NotConfiguredError(detail || "Google Calendar isn't connected yet.");
    if (code === "not_connected") throw new Error(detail || "Google Calendar needs to be reconnected from Admin.");
    if (code === "forbidden") throw new Error(detail || "Your account can't do that.");
    if (code === "unauthorized") throw new Error("You've been signed out — sign back in and try again.");
    if (code === "weekend") throw new Error("Builds aren't scheduled on weekends — pick a weekday.");
    if (code === "same_date") throw new Error("That's already the build date.");
    if (code === "wrong_stage") throw new Error(detail || "This deal isn't at a scheduled build.");
    if (code === "already_built") throw new Error("This build is already marked complete.");
    if (code === "already_scheduled") throw new Error(detail || "This build is already on the calendar. Use Reschedule build to move it.");
    if (code === "plan_required") throw new Error(detail || "Pick how the first payment will be collected.");
    if (code === "note_required") throw new Error(detail || "Type the reason for the Other plan.");
    if (code === "past_date") throw new Error("That date has already passed.");
    if (code === "stage_changed") throw new Error(detail || "Someone moved this deal while you were scheduling. Reopen it and try again.");
    throw new Error(detail || code || `Request failed (HTTP ${res.status}).`);
  }
  return out as unknown as T;
}

/** Thrown when the calendar integration isn't set up; callers fall back to the template link. */
export class NotConfiguredError extends Error {}

export async function fetchCalendarStatus(): Promise<CalendarStatus> {
  return buildCalendar<CalendarStatus>({ action: "status" });
}

export async function fetchBuildCalendarLink(dealId: string): Promise<BuildCalendarLink | null> {
  const { data, error } = await supabase.from("build_calendar_events").select("*").eq("deal_id", dealId).maybeSingle();
  if (error) { console.warn("[buildCalendar] link lookup failed:", error.message); return null; }
  return (data as BuildCalendarLink | null) ?? null;
}

/** The open call-off on a deal (called off, no new date yet), or null. */
export async function fetchOpenCallOff(dealId: string): Promise<BuildReschedule | null> {
  const { data, error } = await supabase.from("build_reschedules").select("*")
    .eq("deal_id", dealId).is("new_build_date", null).is("resolved_at", null)
    .order("created_at", { ascending: false }).limit(1).maybeSingle();
  if (error) { console.warn("[buildCalendar] call-off lookup failed:", error.message); return null; }
  return (data as BuildReschedule | null) ?? null;
}

/** Open call-offs for many deals at once (PM Board list). */
export async function fetchOpenCallOffs(dealIds: string[]): Promise<Record<string, BuildReschedule>> {
  if (dealIds.length === 0) return {};
  const { data, error } = await supabase.from("build_reschedules").select("*")
    .in("deal_id", dealIds).is("new_build_date", null).is("resolved_at", null)
    .order("created_at", { ascending: false });
  if (error) { console.warn("[buildCalendar] call-offs lookup failed:", error.message); return {}; }
  const out: Record<string, BuildReschedule> = {};
  for (const r of (data ?? []) as BuildReschedule[]) if (!out[r.deal_id]) out[r.deal_id] = r;
  return out;
}

export async function fetchRecentReschedules(dealId: string, limit = 5): Promise<BuildReschedule[]> {
  const { data, error } = await supabase.from("build_reschedules").select("*")
    .eq("deal_id", dealId).order("created_at", { ascending: false }).limit(limit);
  if (error) { console.warn("[buildCalendar] history lookup failed:", error.message); return []; }
  return (data ?? []) as BuildReschedule[];
}

// ── date helpers (browser side; the server has its own copy in eventText.ts) ──

/** Midday anchor so a date-only string never slips a day (the May 9 trap). */
function noon(iso: string): Date { return new Date(`${iso.slice(0, 10)}T12:00:00`); }

export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDaysISO(iso: string, days: number): string {
  const d = noon(iso);
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

export function isWeekendISO(iso: string): boolean {
  const dow = noon(iso).getDay();
  return dow === 0 || dow === 6;
}

/** SRS Marietta delivers Monday–Friday only (Greg, Oct 16, 2026): the default
 *  material drop is the last weekday before the build — Tue–Fri builds drop
 *  the day before, a Monday build drops the Friday before. Empty or invalid
 *  input → "". Server copy: supabase/functions/build-calendar/eventText.ts. */
export function srsDropDateISO(buildISO: string | null | undefined): string {
  if (!buildISO || isNaN(noon(buildISO).getTime())) return "";
  let d = addDaysISO(buildISO, -1);
  while (isWeekendISO(d)) d = addDaysISO(d, -1);
  return d;
}

/** The next N weekdays after `fromISO` (exclusive), for the quick-pick chips. */
export function nextWeekdays(fromISO: string, n: number): string[] {
  const out: string[] = [];
  let cur = fromISO;
  while (out.length < n) {
    cur = addDaysISO(cur, 1);
    if (!isWeekendISO(cur)) out.push(cur);
  }
  return out;
}

/** "Thu, Oct 23" */
export function fmtShortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return noon(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/** "Thursday, October 23" */
export function fmtLongDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return noon(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

// ── builds that run more than one day (Oct 26, 2026) ──
// deals.build_scheduled_date is the first day, deals.build_end_date the last
// (null = one day). The rules live in eventText.ts (re-exported above).

/** "Thu, Oct 23", or "Thu, Oct 23 – Fri, Oct 24" for a multi-day build. */
export function fmtBuildSpan(start: string | null | undefined, end: string | null | undefined): string {
  if (!start) return "";
  const last = normalizeBuildEndLocal(start, end);
  return last ? `${fmtShortDate(start)} – ${fmtShortDate(last)}` : fmtShortDate(start);
}

/** "Oct 23", or "Oct 23–24" / "Oct 30 – Nov 2" (the PM Board's compact chip). */
export function fmtBuildSpanCompact(start: string | null | undefined, end: string | null | undefined): string {
  if (!start) return "";
  const md = (iso: string) => noon(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const last = normalizeBuildEndLocal(start, end);
  if (!last) return md(start);
  return last.slice(0, 7) === start.slice(0, 7) ? `${md(start)}–${Number(last.slice(8, 10))}` : `${md(start)} – ${md(last)}`;
}

/** How far back to look for builds that started earlier and still cover a day. */
export const BUILD_SPAN_LOOKBACK_DAYS = 31;

/** The working days in [fromISO, toISO] a build covers, each with "day n of N".
 *  A one-day build covers its start; weekends inside a span are skipped. */
export function buildDaysIn(
  start: string | null | undefined, end: string | null | undefined, fromISO: string, toISO: string,
): Array<{ iso: string; day: number; of: number }> {
  if (!start) return [];
  const first = start.slice(0, 10);
  const last = normalizeBuildEndLocal(first, end) ?? first;
  const days: string[] = [];
  for (let d = first; d <= last; d = addDaysISO(d, 1)) if (d === first || !isWeekendISO(d)) days.push(d);
  return days
    .map((iso, i) => ({ iso, day: i + 1, of: days.length }))
    .filter((x) => x.iso >= fromISO && x.iso <= toISO);
}

/** "Day 2 of 2" on a multi-day build's day, "" otherwise. */
export function buildDayLabel(start: string | null | undefined, end: string | null | undefined, dayISO: string): string {
  const hit = buildDaysIn(start, end, dayISO, dayISO)[0];
  return hit && hit.of > 1 ? `Day ${hit.day} of ${hit.of}` : "";
}

/** Does the build cover this day (its start, or a weekday up to its last day)? */
export function buildCovers(start: string | null | undefined, end: string | null | undefined, dayISO: string): boolean {
  return buildDaysIn(start, end, dayISO, dayISO).length > 0;
}

function normalizeBuildEndLocal(start: string, end: string | null | undefined): string | null {
  const e = end ? end.slice(0, 10) : "";
  return e && e > start.slice(0, 10) ? e : null;
}

/** Local calendar day as YYYY-MM-DD (not UTC: 8 PM ET must still be today). */
export function todayISO(): string { return toISODate(new Date()); }

/** Google Calendar day view, for the "edit it by hand" fallback. */
export function calendarDayUrl(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `https://calendar.google.com/calendar/r/day/${y}/${Number(m)}/${Number(d)}`;
}
